import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../App.css';

const OrderStatusPage = () => {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const email = searchParams.get('email');
  const orderId = searchParams.get('orderId');

  useEffect(() => {
    fetchOrderDetails();
  }, [email, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError('');

    // Validate that both email and orderId are provided
    if (!email || !orderId) {
      setError('Please provide both email and order ID');
      setLoading(false);
      return;
    }

    try {
      const { orderService } = await import('../services/api');
      const response = await orderService.getById(orderId, email);
      const orderData = response.data.order;

      const formattedOrder = {
        id: orderData.order_number,
        email: orderData.customer_email,
        date: new Date(orderData.created_at).toLocaleDateString(),
        status: orderData.payment_status || 'completed',
        total: orderData.total_amount,
        items: (orderData.items || []).map(item => ({
          name: item.name,
          platform: item.platform || 'N/A',
          quantity: item.quantity,
          price: item.price,
          imageUrl: item.imageUrl || item.image_url,
          giftCardCode: 'XXXX-XXXX-XXXX-' + Math.random().toString(36).substr(2, 4).toUpperCase()
        })),
        customerInfo: {
          name: orderData.customer_name,
          email: orderData.customer_email,
          phone: orderData.customer_phone || 'N/A',
          region: orderData.customer_region || 'N/A'
        },
        paymentMethod: 'Cryptocurrency',
        transactionId: orderData.payment_data?.txn_id || 'TXN-' + Math.random().toString(36).substr(2, 9).toUpperCase()
      };

      setOrder(formattedOrder);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load order:', err);
      setError('Order not found');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f39c12',
      processing: '#3498db',
      completed: '#27ae60',
      failed: '#e74c3c'
    };
    return colors[status] || '#95a5a6';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Gift card code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="order-status-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-status-page">
        <div className="error-container">
          <h2>❌ Order Not Found</h2>
          <p>We couldn't find an order with the provided details.</p>
          <button onClick={() => navigate('/track-order')} className="back-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-status-page">
      <div className="order-status-container">
        <div className="status-header">
          <h1>✅ Order Confirmed</h1>
          <div className="order-id">Order #{order.id}</div>
          <p style={{ color: '#b0b0b0', marginTop: '10px' }}>Thank you for your purchase! Your order has been successfully processed.</p>
        </div>

        <div className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
          ✓ {order.status.toUpperCase()}
        </div>

        <div className="order-info-grid">
          <div className="info-card">
            <h3>Order Date</h3>
            <p>📅 {order.date}</p>
          </div>
          <div className="info-card">
            <h3>Total Paid</h3>
            <p>💰 ${order.total.toFixed(2)}</p>
          </div>
          <div className="info-card">
            <h3>Payment Method</h3>
            <p>💳 {order.paymentMethod}</p>
          </div>
          <div className="info-card">
            <h3>Transaction ID</h3>
            <p>🔑 {order.transactionId}</p>
          </div>
        </div>

        <div className="customer-details">
          <h3>Customer Information</h3>
          <div className="detail-row">
            <span>👤 Name</span>
            <span>{order.customerInfo.name}</span>
          </div>
          <div className="detail-row">
            <span>📧 Email</span>
            <span>{order.customerInfo.email}</span>
          </div>
          <div className="detail-row">
            <span>📱 Phone</span>
            <span>{order.customerInfo.phone}</span>
          </div>
          <div className="detail-row">
            <span>🌍 Country</span>
            <span>{order.customerInfo.region}</span>
          </div>
        </div>

        <div className="gift-cards-section">
          <h3>Your Products</h3>
          <p style={{ color: '#b0b0b0', marginBottom: '20px', fontSize: '14px' }}>Below are your purchased items with product codes.</p>
          {order.items.map((item, idx) => (
            <div key={idx} className="order-product-item">
              <img src={item.imageUrl || 'https://via.placeholder.com/100'} alt={item.name} className="product-thumbnail" />
              <div className="product-item-details">
                <h4>{item.name}</h4>
                <div className="product-meta">
                  <span className="platform-badge">{item.platform}</span>
                  <span className="product-quantity">Qty: {item.quantity}</span>
                  <span className="product-price">${item.price.toFixed(2)}</span>
                </div>
                <div className="gift-card-code">
                  <span className="code-label">🔑 Product Code</span>
                  <div className="code-display">
                    <code>{item.giftCardCode}</code>
                    <button 
                      onClick={() => copyToClipboard(item.giftCardCode)}
                      className="copy-btn"
                    >
                      Copy Code
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="order-actions">
          <button onClick={() => window.print()} className="print-btn">
            Print Receipt
          </button>
          <button onClick={() => navigate('/')} className="home-btn">
            Continue Shopping
          </button>
        </div>

        <div className="help-section">
          <p>Questions? Contact our support team at <a href="mailto:iwantcheats.store@gmail.com">iwantcheats.store@gmail.com</a></p>
          <p style={{ fontSize: '13px', color: '#999', marginTop: '10px' }}>A confirmation email has been sent to {order.customerInfo.email}</p>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusPage;
